import React, {useCallback, useEffect, useState} from 'react'
import {Pressable, View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {type NavigationProp} from '#/lib/routes/types'
import {useCallState} from '#/state/matrix/hooks/useCallState'
import {useSession} from '#/state/session'
import {useRooms} from '#/screens/Rooms/hooks/useRooms'
import {atoms as a, useTheme} from '#/alf'
import {
  VideoCamera_Filled_Corner0_Rounded as VideoCameraFilled,
  VideoCamera_Stroke2_Corner0_Rounded as VideoCamera,
} from '#/components/icons/VideoCamera'
import {Text} from '#/components/Typography'

let SecureChatCallButton = ({roomId}: {roomId: string}): React.ReactNode => {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const {currentAccount} = useSession()
  const {createRoom} = useRooms()
  const {isCallActive, isStartedByMe, callId, startCall, endCall} =
    useCallState(roomId)
  const [isCreating, setIsCreating] = useState(false)

  // Clear call state when returning from a call
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (isCallActive && isStartedByMe) {
        endCall()
      }
    })
    return unsubscribe
  }, [navigation, isCallActive, isStartedByMe, endCall])

  const onPress = useCallback(async () => {
    if (isCallActive && callId) {
      // Join or rejoin existing call
      navigation.navigate('P2pCall', {callId: callId.replace('p2p-', '')})
      return
    }

    if (isCreating || !currentAccount) return
    setIsCreating(true)
    try {
      // Create a private room in rooms_data
      const room = await createRoom(
        `DM Call: ${currentAccount.handle}`,
        undefined,
        true,
      )
      // Store the room slug in Matrix state event
      await startCall(room.id)
      navigation.navigate('P2pCall', {callId: room.id.replace('p2p-', '')})
    } catch (err) {
      console.error('Failed to create call room:', err)
    } finally {
      setIsCreating(false)
    }
  }, [
    isCallActive,
    callId,
    isCreating,
    currentAccount,
    createRoom,
    startCall,
    navigation,
  ])

  const isJoin = isCallActive && !isStartedByMe
  const label = isJoin ? _(msg`Join call`) : _(msg`Start call`)
  const Icon = isCallActive ? VideoCameraFilled : VideoCamera
  const bgColor = isJoin ? '#22c55e' : t.palette.primary_500

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint=""
      style={({pressed, hovered}: any) => [
        a.flex_row,
        a.align_center,
        a.gap_xs,
        a.rounded_full,
        {
          backgroundColor: bgColor,
          paddingVertical: 6,
          paddingHorizontal: 12,
          opacity: pressed ? 0.8 : hovered ? 0.9 : 1,
        },
      ]}>
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <Text style={[a.text_sm, a.font_bold, {color: t.palette.white}]}>
          {label}
        </Text>
        <Icon width={18} height={18} color={t.palette.white} />
      </View>
    </Pressable>
  )
}
SecureChatCallButton = React.memo(SecureChatCallButton)

export {SecureChatCallButton}
